import { isValidJson } from 'components/form/utilities'
import { updateMultipleFieldsPath, useDataTemplate } from 'components/form_builder/features/data/dataSlice'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useDragAndDropPaths from '../../hooks/useDragAndDropPaths'
import { useMultiplePaths } from '../../hooks/useMultiplePaths'
import { PathListItems, ToggleSwitch } from './Paths'

const LIST_TOGGLE = [
  { id: 'list', label: 'list', component: <i className="fa-solid fa-list"></i> },
  { id: 'code', label: 'code', component: <i className="fa-solid fa-code"></i> },
]

const arePathsEqual = (pathsA, pathsB) => {
  if (pathsA.length !== pathsB.length) return false
  const sortedA = [...pathsA].sort()
  const sortedB = [...pathsB].sort()
  return sortedA.every((path, index) => path === sortedB[index])
}

export const OverrideWarning = ({ onOverride }) => (
  <div style={{ width: 'fit-content' }} className="dome-d-flex dome-gap6 dome-flex-column">
    <div className="dome-d-flex dome-gap3 dome-align-center">
      <span>
        <i className="fa-light fa-circle-exclamation dome-color-red-alert"></i>
      </span>
      <span className="dome-p3 dome-color-red-alert">Warning</span>
      <span className="dome-p3 dome-color-med-grey">Mixed values</span>
    </div>
    <button type="button" onClick={onOverride} className="dome-btn dome-btn-base dome-btn-red-stroke">
      Override Values
    </button>
  </div>
)

const TextArea = ({ component, components }) => {
  const dispatch = useDispatch()
  const template = useDataTemplate(component.type, component.id)
  const textAreaRef = useRef(null)

  const [input, setInput] = useState('')
  const isValid = useMemo(() => isValidJson(input), [input])

  useEffect(() => {
    if (template.paths?.length) {
      const paths = template.paths.map((e) => e.path)
      const pathsString = JSON.stringify(paths, null, paths.length)
      setInput(pathsString)
    }
  }, [template.paths])

  const handleInputChange = (e) => setInput(e.target.value)

  const storeIfValid = useCallback(() => {
    try {
      if (!isValidJson(input)) return
      const curPaths = JSON.parse(input)
      const templatePaths = new Set(template.templatePaths)
      const newPaths = curPaths.map((path) => ({ path, type: templatePaths.has(path) ? 'templatePath' : 'fillPath' }))
      dispatch(updateMultipleFieldsPath({ components, paths: newPaths }))
    } catch (e) {}
  }, [input, components, template])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.code) {
          case 'KeyS':
            e.preventDefault()
            storeIfValid()
            break
        }
      }
      if (e.target === textAreaRef.current) e.stopPropagation()
    },
    [storeIfValid]
  )

  return (
    <textarea
      ref={textAreaRef}
      value={input || ''}
      className="scrollable"
      onBlur={storeIfValid}
      onKeyDown={handleKeyDown}
      onChange={handleInputChange}
      style={{ borderColor: isValid ? '#898989' : '#d7272d' }}
    />
  )
}

export default function PathForMultipleFields({ components }) {
  const dispatch = useDispatch()
  const data = useSelector((state) => state.data)

  const [activeOption, setActiveOption] = useState('list')
  const [hasOverridden, setHasOverridden] = useState(false)

  const isUniform = useMemo(() => {
    if (!components || components.length === 0) return true
    const firstComponentPaths = data[components[0].type]?.[components[0].id]?.paths?.map((p) => p.path) ?? []

    return components.slice(1).every((component) => {
      const currentPaths = data[component.type]?.[component.id]?.paths?.map((p) => p.path) ?? []
      return arePathsEqual(firstComponentPaths, currentPaths)
    })
  }, [components, data])

  const isEditable = isUniform || hasOverridden

  const initialPaths = useMemo(() => {
    if (isUniform) {
      const firstComponent = components[0]
      const commonPaths = data[firstComponent.type]?.[firstComponent.id]?.paths ?? []
      return commonPaths.length > 0 ? commonPaths : [{ type: 'fillPath', path: '' }]
    }
    return [{ type: 'fillPath', path: '' }]
  }, [isUniform, components, data])

  const handleSave = useCallback(
    (processedPaths) => dispatch(updateMultipleFieldsPath({ components, paths: processedPaths })),
    [components]
  )

  const { paths, setPaths, handleChange, handleRemove, handleAdd, handleBlur, handleKeyDown, handleKeyUp } =
    useMultiplePaths(initialPaths, handleSave)

  const onDragOver = useCallback((newPaths) => setPaths(newPaths), [setPaths])
  const onDragEnd = useCallback(() => handleSave(paths), [paths, handleSave])

  const { handleDragStart, handleDragEnter, handleDragEnd } = useDragAndDropPaths({ paths, onDragOver, onDragEnd })

  const handleOverride = () => {
    setHasOverridden(true)
    setPaths([{ type: 'fillPath', path: '' }])
    dispatch(updateMultipleFieldsPath({ components, paths: [] }))
  }

  const handleToggle = (id) => setActiveOption(id)

  return (
    <div className="section-divider">
      <div className="section-name dome-d-flex dome-align-center dome-justify-between">
        <span className="dome-p2 dome-color-darkest-grey dome-text-w500">Fill</span>
        {isEditable && <ToggleSwitch onSelect={handleToggle} selected={activeOption} options={LIST_TOGGLE} />}
      </div>

      {isEditable ? (
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <div className="dome-d-flex dome-flex-column">
            <span className="dome-p3 dome-color-med-grey">Paths are filled in the order below.</span>
            <span className="dome-p3 dome-color-med-grey">
              Add any other relevant paths for this field, then reorder if necessary.
            </span>
          </div>
          {activeOption === 'list' ? (
            <PathListItems
              paths={paths}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              onKeyDown={handleKeyDown}
              addNewPath={() => handleAdd({ type: 'fillPath', path: '' })}
              removePath={handleRemove}
              onDragEnd={onDragEnd}
              onDragStart={handleDragStart}
              onDragEnter={handleDragEnter}
            />
          ) : (
            <TextArea component={components[0]} components={components} />
          )}
        </div>
      ) : (
        <OverrideWarning onOverride={handleOverride} />
      )}
    </div>
  )
}
